/**
 * Klávesové skratky celej appky.
 *
 * Logika je tu, bez Reactu, aby sa dala otestovať tak, ako ju zažije človek:
 * stláčanie kláves nad rôznymi prvkami. Najdôležitejšie pravidlo je to, kedy sa
 * skratka NESMIE spustiť — keď človek píše. „C" v komentári nesmie otvoriť nový
 * ticket a „G" v nadpise nesmie prehodiť stránku; to by bolo horšie než žiadne
 * skratky.
 */

export type ShortcutGroup = { title: string; items: Array<{ keys: string[]; label: string }> };

/** Čo ukáže prehľad `?`. Jediný zoznam, aby prehľad a správanie nešli od seba. */
export const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Všade',
    items: [
      { keys: ['/'], label: 'Hľadať' },
      { keys: ['['], label: 'Zúžiť alebo rozbaliť bočný panel' },
      { keys: ['C'], label: 'Nový ticket (na doske projektu)' },
      { keys: ['?'], label: 'Prehľad skratiek' },
    ],
  },
  {
    title: 'Prejsť na',
    items: [
      { keys: ['G', 'D'], label: 'Prehľad' },
      { keys: ['G', 'P'], label: 'Projekty' },
      { keys: ['G', 'I'], label: 'Schránka' },
      { keys: ['G', 'T'], label: 'Úlohy' },
    ],
  },
];

/** Kam vedie druhé písmeno po „G". */
export const GO_TARGETS: Readonly<Record<string, string>> = {
  d: '/',
  p: '/projects',
  i: '/schranka',
  t: '/plan?tab=zoznam',
};

/** Po „G" sa na druhé písmeno čaká takto dlho; potom sa „G" zabudne. */
export const SEQUENCE_TIMEOUT_MS = 1500;

/**
 * Či prvok prijíma text. Nad takým prvkom sa skratky nespúšťajú nikdy.
 *
 * Aj `select`: písmeno v ňom vyberá položku podľa začiatku názvu. Aj prvok
 * vnútri `contenteditable`, nielen ten s atribútom — udalosť prichádza z
 * miesta, kde je kurzor.
 */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!target || typeof (target as Element).closest !== 'function') {
    return false;
  }
  const element = target as HTMLElement;
  if (element.isContentEditable) {
    return true;
  }
  if (element.closest('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) {
    return true;
  }
  const tag = element.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  if (tag === 'INPUT') {
    const type = ((element as HTMLInputElement).type || 'text').toLowerCase();
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(type);
  }
  return false;
};

/**
 * Otvorené modálne okno (detail tiketu, formulár, hľadanie) si klávesy rieši
 * samo. Skratka pod ním by menila stránku za jeho chrbtom.
 */
const modalOpen = (doc: Document): boolean =>
  Boolean(doc.querySelector('[aria-modal="true"], [role="dialog"][data-headlessui-state~="open"]'));

export type ShortcutActions = {
  navigate: (to: string) => void;
  openSearch: () => void;
  openHelp: () => void;
  /** Nový ticket. Vráti `false`, keď sa na aktuálnej obrazovke nedá. */
  newTicket: () => boolean;
  /** Keď „C" nie je kde použiť — napr. ukázať zoznam projektov. */
  newTicketElsewhere: () => void;
  /** Bočný panel na ikony a späť. Bez neho sa „[“ nechá tak. */
  toggleSidebar?: () => void;
};

export type ShortcutHandler = (event: KeyboardEvent) => boolean;

/**
 * Obsluha `keydown`. Vráti `true`, keď kláves spotrebovala (a zavolala
 * `preventDefault`), inak ho nechá tak, ako bol.
 */
export const createShortcutHandler = (
  actions: ShortcutActions,
  options: { now?: () => number; doc?: Document } = {},
): ShortcutHandler => {
  const now = options.now ?? (() => Date.now());
  let pendingG: number | null = null;

  return (event) => {
    const doc = options.doc ?? (typeof document === 'undefined' ? null : document);

    // „[“ je na slovenskej klávesnici pod AltGr (Windows hlási Ctrl+Alt) alebo
    // Option (Mac), takže Alt ho nesmie zablokovať ako pri písmenách. Cmd a
    // samotný Ctrl áno — to sú skratky prehliadača.
    if (
      event.key === '[' &&
      actions.toggleSidebar &&
      !event.metaKey &&
      !(event.ctrlKey && !event.altKey) &&
      !event.defaultPrevented &&
      !event.isComposing &&
      !event.repeat &&
      !isTypingTarget(event.target) &&
      !(doc !== null && (isTypingTarget(doc.activeElement) || modalOpen(doc)))
    ) {
      pendingG = null;
      actions.toggleSidebar();
      event.preventDefault();
      return true;
    }

    if (
      event.defaultPrevented ||
      event.isComposing ||
      event.repeat ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      isTypingTarget(event.target) ||
      (doc !== null && isTypingTarget(doc.activeElement))
    ) {
      pendingG = null;
      return false;
    }
    if (doc !== null && modalOpen(doc)) {
      pendingG = null;
      return false;
    }

    const key = event.key;
    const consume = () => {
      event.preventDefault();
      return true;
    };

    // „?" aj „/" sú na slovenskej klávesnici so Shiftom, takže Shift sa pri
    // nich nekontroluje. Pri písmenách áno: Shift+C nie je skratka.
    if (key === '?') {
      pendingG = null;
      actions.openHelp();
      return consume();
    }
    if (key === '/') {
      pendingG = null;
      actions.openSearch();
      return consume();
    }
    if (event.shiftKey || key.length !== 1) {
      pendingG = null;
      return false;
    }

    const letter = key.toLowerCase();
    if (pendingG !== null) {
      const fresh = now() - pendingG <= SEQUENCE_TIMEOUT_MS;
      pendingG = null;
      const target = GO_TARGETS[letter];
      if (fresh && target) {
        actions.navigate(target);
        return consume();
      }
      if (fresh) {
        // „G" a nezmysel: sekvencia sa zruší a písmeno sa nespracuje ako
        // samostatná skratka — „G C" nemá nečakane otvoriť nový ticket.
        return false;
      }
    }

    if (letter === 'g') {
      pendingG = now();
      return consume();
    }
    if (letter === 'c') {
      if (!actions.newTicket()) {
        actions.newTicketElsewhere();
      }
      return consume();
    }
    return false;
  };
};

/**
 * Zariadenie iba s dotykom (telefón bez klávesnice). Tam sa skratky vôbec
 * nezapínajú — nemá ich kto stlačiť a nemá zmysel ich počúvať.
 */
export const isTouchOnlyDevice = (matchMedia: ((query: string) => MediaQueryList) | undefined): boolean => {
  if (typeof matchMedia !== 'function') {
    return false;
  }
  try {
    return (
      matchMedia('(hover: none)').matches &&
      matchMedia('(pointer: coarse)').matches &&
      !matchMedia('(any-pointer: fine)').matches
    );
  } catch {
    return false;
  }
};

// --- „C" na doske -------------------------------------------------------------
//
// Nový ticket vie založiť iba doska projektu, a tá o skratkách nemá čo vedieť.
// Preto sa sem len prihlási jednou funkciou (`useNewTicketShortcut`) a pri
// odchode sa odhlási. Posledná prihlásená vyhráva — keby boli dosky dve,
// platí tá, ktorá sa ukázala naposledy.
const newTicketActions: Array<() => void> = [];

export const registerNewTicketAction = (action: () => void): (() => void) => {
  newTicketActions.push(action);
  return () => {
    const index = newTicketActions.lastIndexOf(action);
    if (index !== -1) {
      newTicketActions.splice(index, 1);
    }
  };
};

export const runNewTicketAction = (): boolean => {
  const action = newTicketActions[newTicketActions.length - 1];
  if (!action) {
    return false;
  }
  action();
  return true;
};
